/**
 * Flowy object icon and route mapping utility
 * Maps API URL patterns to their corresponding icons and edit routes
 */

// Icon and route configuration for each Flowy object type (alphabetically sorted)
const FLOWY_OBJECT_CONFIG = {
  'ai-agent': {
    icon: 'mdi-robot-happy',
    url: '/aiAgents/edit/'
  },
  'application': {
    icon: 'mdi-application-outline',
    url: '/applications/edit/'
  },
  'entity': {
    icon: 'mdi-database-settings-outline',
    url: '/entities/edit/'
  },
  'flow': {
    icon: 'mdi-transit-connection-variant',
    url: '/flow/edit/'
  },
  'global-translation': {
    icon: 'mdi-text',
    url: '/templates/translations/edit/'
  },
  'import-history': {
    icon: 'mdi-application-import',
    url: '/import-history/edit/'
  },
  'library': {
    icon: 'mdi-library-outline',
    url: '/libraries/edit/'
  },
  'module': {
    icon: 'mdi-briefcase-edit-outline',
    url: '/modules/local/edit/'
  },
  'plugin': {
    icon: 'mdi-puzzle-outline',
    url: '/plugins/edit/'
  },
  'process': {
    icon: 'mdi-console-network',
    url: '/processes/edit/'
  },
  'process-credential': {
    icon: 'mdi-lock',
    url: '/credentials/edit/'
  },
  'role': {
    icon: 'mdi-account-group-outline',
    url: '/access/roles/edit/'
  },
  'screen': {
    icon: 'mdi-television',
    url: '/screen/edit/'
  },
  'setting': {
    icon: 'mdi-cog',
    url: '/settings/edit/'
  },
  'static-resource': {
    icon: 'mdi-image',
    url: '/static-resources/edit/'
  },
  'storage': {
    icon: 'mdi-cube',
    url: '/storage/edit/'
  },
  'text-template': {
    icon: 'mdi-text-box-outline',
    url: '/templates/templates/edit/'
  },
  'translation': {
    icon: 'mdi-text',
    url: '/templates/translations/edit/'
  },
  'trigger-cron': {
    icon: 'mdi-briefcase-clock-outline',
    url: '/triggers/cron/edit/'
  },
  'trigger-event-handler': {
    icon: 'mdi-briefcase-outline',
    url: '/triggers/event-handler/edit/'
  },
  'trigger-mcp': {
    icon: 'mdi-server',
    url: '/triggers/mcp/edit/'
  },
  'trigger-messaging': {
    icon: 'mdi-message-text-outline',
    url: '/triggers/messaging/edit/'
  },
  'trigger-rest': {
    icon: 'mdi-web',
    url: '/triggers/rest/edit/'
  },
  'trigger-ui': {
    icon: 'mdi-gesture-tap',
    url: '/triggers/ui/edit/'
  },
  'trigger-whatsapp': {
    icon: 'mdi-whatsapp',
    url: '/triggers/whatsapp/edit/'
  },
  'user': {
    icon: 'mdi-account',
    url: '/access/users/edit/'
  },
  'validation-rule': {
    icon: 'mdi-magnify-scan',
    url: '/validations/edit/'
  }
}

// Map enum-style types (UPPER_SNAKE_CASE) to config keys (alphabetically sorted)
// Used by ModuleDetails.vue which receives types like 'TRIGGER_CRON', 'PROCESS'
const ENUM_TO_KEY = {
  'AI_AGENT': 'ai-agent',
  'APPLICATION': 'application',
  'ENTITY': 'entity',
  'FLOW': 'flow',
  'GLOBAL_TRANSLATION': 'global-translation',
  'IMPORT_HISTORY': 'import-history',
  'LIBRARY': 'library',
  'MODULE': 'module',
  'PLUGIN': 'plugin',
  'PROCESS': 'process',
  'PROCESS_CREDENTIAL': 'process-credential',
  'ROLE': 'role',
  'SCREEN': 'screen',
  'SETTING': 'setting',
  'STATIC_RESOURCE': 'static-resource',
  'STORAGE': 'storage',
  'TEXT_TEMPLATE': 'text-template',
  'TRANSLATION': 'translation',
  'TRIGGER_CRON': 'trigger-cron',
  'TRIGGER_EVENT_HANDLER': 'trigger-event-handler',
  'TRIGGER_MCP': 'trigger-mcp',
  'TRIGGER_MESSAGING': 'trigger-messaging',
  'TRIGGER_REST': 'trigger-rest',
  'TRIGGER_UI': 'trigger-ui',
  'TRIGGER_WHATSAPP': 'trigger-whatsapp',
  'USER': 'user',
  'VALIDATION_RULE': 'validation-rule'
}

// Match order for URL pattern matching
// CRITICAL: Order matters for substring matching!
// - 'process-credential' MUST come before 'process' (otherwise 'process' matches both)
// - 'global-translation' naturally comes before 'translation' alphabetically
// Otherwise alphabetically sorted for maintainability
const MATCH_ORDER = [
  'ai-agent',
  'application',
  'entity',
  'flow',
  'global-translation',
  'import-history',
  'library',
  'module',
  'plugin',
  'process-credential',  // MUST come before 'process'
  'process',
  'role',
  'screen',
  'setting',
  'static-resource',
  'storage',
  'text-template',
  'translation',
  'trigger-cron',
  'trigger-event-handler',
  'trigger-mcp',
  'trigger-messaging',
  'trigger-rest',
  'trigger-ui',
  'trigger-whatsapp',
  'user',
  'validation-rule'
]

/**
 * Get Flowy object icon and route based on API URL type (synchronous)
 * @param {string} type - API URL or type identifier
 * @returns {{icon: string, url: string}|null} Config object (read-only) or null if not found
 */
export function getFlowyObjectIcon(type) {
  if (!type) return null

  for (const key of MATCH_ORDER) {
    if (type.includes(key)) {
      return FLOWY_OBJECT_CONFIG[key]
    }
  }

  return null
}

/**
 * Get Flowy object icon by enum-style type (for ModuleDetails.vue)
 * @param {string} enumType - Enum type like 'TRIGGER_CRON', 'PROCESS', etc.
 * @returns {string} Icon string or empty string if not found
 */
export function getFlowyObjectIconByEnum(enumType) {
  if (!enumType) return ''
  const key = ENUM_TO_KEY[enumType]
  return key ? FLOWY_OBJECT_CONFIG[key]?.icon || '' : ''
}

/**
 * Get icon for a resource type (handles both enum and kebab-case types)
 * Used by components that need icon mappings for resource types
 * @param {string} type - Resource type (e.g., 'MODULE', 'PROCESS', 'modules', 'processes')
 * @param {string} subType - Optional subtype for settings (FRONTEND, INSTANCE, PROCESS, PLUGIN)
 * @returns {string} MDI icon string
 */
export function getIconForType(type, subType = null) {
  if (!type) return 'mdi-home'

  // Handle settings subtypes
  if (type === 'SETTING' || type === 'setting' || type === 'settings') {
    if (subType === 'FRONTEND') return 'mdi-flip-to-front'
    if (subType === 'INSTANCE') return 'mdi-flip-to-back'
    if (subType === 'PROCESS') return 'mdi-flip-vertical'
    if (subType === 'PLUGIN') return 'mdi-puzzle-star-outline'
  }

  // Normalize type to UPPER_SNAKE_CASE for enum lookup
  const normalizedType = type.toUpperCase().replace(/-/g, '_')

  // Check if it's an enum type
  const configKey = ENUM_TO_KEY[normalizedType]
  if (configKey && FLOWY_OBJECT_CONFIG[configKey]) {
    return FLOWY_OBJECT_CONFIG[configKey].icon
  }

  // Additional mappings for route-based types not in ENUM_TO_KEY
  const routeTypeMap = {
    'MODULES': 'module',
    'LOCAL': 'module',
    'CREDENTIALS': 'process-credential',
    'TEMPLATES': 'text-template',
    'TRANSLATIONS': 'translation',
    'VALIDATIONS': 'validation-rule',
    'SETTINGS': 'setting',
    'USERS': 'user',
    'ROLES': 'role',
    'TAGS': 'TAG',
    'LIBRARIES': 'library',
    'STORAGE': 'storage',
    'ENTITIES': 'entity',
    'APPLICATIONS': 'application',
    'AIAGENTS': 'ai-agent',
    'SCREEN': 'screen',
    'CRON': 'trigger-cron',
    'REST': 'trigger-rest',
    'UI': 'trigger-ui',
    'FLOW': 'flow',
    'MCP': 'trigger-mcp',
    'EVENT_HANDLER': 'trigger-event-handler',
    'MESSAGING': 'trigger-messaging',
    'WHATSAPP': 'trigger-whatsapp',
    'PROCESSES': 'process',
    'PLUGINS': 'plugin',
    'IMPORT_HISTORY': 'import-history',
    'HISTORY': 'HISTORY'
  }

  const mappedKey = routeTypeMap[normalizedType]
  if (mappedKey) {
    // Handle special cases
    if (mappedKey === 'TAG') return 'mdi-tag'
    if (mappedKey === 'HISTORY') return 'mdi-history'

    if (FLOWY_OBJECT_CONFIG[mappedKey]) {
      return FLOWY_OBJECT_CONFIG[mappedKey].icon
    }
  }

  // Fallback to default icon
  return 'mdi-home'
}

export { FLOWY_OBJECT_CONFIG, MATCH_ORDER, ENUM_TO_KEY }
